import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { emsApi } from '@/api/emsApi';
import { SplashscreenComponent } from '@/app/shell/AuthenticationPageLayout';
import { PsapConfigurationContextProvider, usePsapConfiguration } from '@/app/shell/psapConfiguration';
import type { PsapConfigurationDto } from '@/app/shell/psapConfiguration';
import { NgButton } from '@/ui';
import { PsapConfigurationContext as UiPsapConfigurationContext } from '@/ui/psap/PsapConfigurationContext';

const DEFAULT_FAVICON = '/Images/Icons/icon-72x72.png';

async function fetchPsapConfiguration(): Promise<PsapConfigurationDto> {
  const { data, response } = await emsApi.GET('/portal/v1/configuration/portal');

  if (data === undefined) {
    throw new Error(`Failed to load PSAP configuration (HTTP ${response.status})`);
  }

  return data as PsapConfigurationDto;
}

export function PsapConfigurationProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const {
    data: psapConfiguration,
    isError,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ['configuration', 'portal'],
    queryFn: fetchPsapConfiguration,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const psapLogoIcon = psapConfiguration
    ? `${psapConfiguration.psapPortalConfiguration.logoFolderPath}/logo.ico`
    : DEFAULT_FAVICON;
  const themeColor = psapConfiguration?.psapPortalConfiguration.themeColor ?? '';

  return (
    <>
      <link rel="icon" href={psapLogoIcon} type="image/x-icon" />
      <meta name="msapplication-TileColor" content={themeColor} />
      <meta name="theme-color" content={themeColor} />

      {psapConfiguration && (
        <style>{`:root {
    --psap-color: ${psapConfiguration.psapPortalConfiguration.themeColor};
}`}</style>
      )}

      {psapConfiguration ? (
        <PsapConfigurationContextProvider value={psapConfiguration}>
          <UiPsapConfigurationContext.Provider value={psapConfiguration}>
            {children}
          </UiPsapConfigurationContext.Provider>
        </PsapConfigurationContextProvider>
      ) : isError ? (
        <SplashscreenComponent>
          <div>
            <p role="alert">{t('Loading_GeneralLoadingFailedMessage')}</p>
            <div>
              <NgButton
                text={t('Button_TryAgain')}
                icon="fal fa-rotate-right"
                disabled={isFetching}
                onClick={() => {
                  void refetch();
                }}
              />
            </div>
          </div>
        </SplashscreenComponent>
      ) : (
        <SplashscreenComponent>
          <p className="fade-in-delay">Loading PSAP Configuration</p>
        </SplashscreenComponent>
      )}
    </>
  );
}

type PsapViewProps = {
  psapId?: string;
  psapIds?: readonly string[];
  children: ReactNode;
};

export function PsapView({ psapId, psapIds, children }: PsapViewProps) {
  const psapConfiguration = usePsapConfiguration();

  const effectivePsapIds = [...(psapIds ?? []), ...(psapId !== undefined ? [psapId] : [])];
  if (effectivePsapIds.length === 0) {
    throw new Error('At least one of the parameters psapId or psapIds must be set.');
  }

  const isVisible = effectivePsapIds.some(
    (id) => id.toLowerCase() === psapConfiguration.id.toLowerCase(),
  );

  return isVisible ? <>{children}</> : null;
}
